import log from 'loglevel';
// import ImmutablePropTypes from 'react-immutable-proptypes';
import * as React from 'react';
import { Component } from 'react';
import { connect } from 'react-redux';
import { List, Set } from 'immutable';
import * as PropTypes from 'prop-types';
import {
    toggleFullScreen,
    toggleChat,
    openChat,
    closeChat,
    showChat,
    addUserMessage,
    emitUserMessage,
    addResponseMessage,
    addCarousel,
    addVideoSnippet,
    addImageSnippet,
    addButtons,
    renderCustomComponent,
    initialize,
    connectServer,
    disconnectServer,
    pullSession,
    newUnreadMessage,
    triggerMessageDelayed,
    triggerTooltipSent,
    showTooltip,
    clearMetadata,
    setUserInput,
    setLinkTarget,
    setPageChangeCallbacks,
    changeOldUrl,
    setDomHighlight,
    evalUrl,
    setCustomCss,
    setSessionId,
    //registerAuthtokenChangeHandler
} from '../../store/actions';
import { behavior as behaviorSelectors, config as configSelectors } from '../../store/selectors';
import { safeQuerySelectorAll } from '../../utils/dom';
import { MESSAGES_TYPES, SESSION_NAME, NEXT_MESSAGE } from '../../constants';
import WidgetLayout from './layout';
import { storeLocalSession, getLocalSession } from '../../store/reducers/helper';
import { LocationInfo, CustomerId, UserId } from '../../types';
import { TasksBackendClient, TaskType, GreetingTaskEventType } from '../../backends/tasks';
import ThemeContext from './ThemeContext';
import {
    sendEvent,
    buildEventPayload,
    wasSessionStartSent,
    markSessionStartSent,
} from '../../api/analytics-client';

export type WidgetProps = {
    connectOn: string;
    autoClearCache: boolean;
    storage: Storage;
    dispatch: React.Dispatch<any>;
    defaultHighlightAnimation: string;
    isChatOpen: boolean;
    embedded: boolean;
    overlayMode: boolean;
    chatWindowInsetX?: string;
    chatWindowInsetY?: string;
    disablePreviewBubble?: boolean;
    mobileFullScreenOnFocus?: boolean;
    pageUrl?: string;
    analyticsReferrer?: string;
    initialized: boolean;
    socket: any;
    messages: List<string>;
    disableTooltips: boolean;
    customMessageDelay: Function;
    domHighlight: any;
    defaultHighlightClassname: string;
    defaultHighlightCss: string;
    tooltipPayload: string;
    tooltipDelay: number;
    welcomeMessages: Array<string>;
    initPayload: string;
    customData: any;
    isChatVisible: boolean;
    connected: boolean;
    tooltipSent: any;
    pendingGreetings: List<any>;
    showingGreetings: Set<string>;
    tasksBackendClient: TasksBackendClient;
    browserLocation: LocationInfo;
    userInfoExists: boolean;
    customerId: CustomerId;
    userId: UserId;
    customComponent: Function;
    title: string | Component;
    subtitle: string | Component;
    showCloseButton: boolean;
    showFullScreenButton: boolean;
    hideWhenNotConnected: boolean;
    fullScreenMode: boolean;
    badge: number;
    params: any;
    openLauncherImage: string;
    inputTextFieldHint: string;
    closeImage: string;
    displayUnreadCount: boolean;
    showMessageDate: boolean | (() => boolean);
    analyticsServiceUrl: string | null;
    sessionId: string | null;
};

class Widget extends Component<WidgetProps> {
    static propTypes: any;
    static defaultProps: any;

    private messages: Array<string>;
    private delayedMessage: string | null;
    private messageDelayTimeout: ReturnType<typeof global.setTimeout> | null;
    private onGoingMessageDelay: boolean;
    private intervalId: ReturnType<typeof global.setInterval> | null;
    private eventListenerCleaner: () => void;
    private tooltipTimeout: ReturnType<typeof global.setTimeout> | null;

    constructor(props: WidgetProps) {
        super(props);

        this.messages = [];
        this.delayedMessage = null;
        this.messageDelayTimeout = null;
        this.onGoingMessageDelay = false;
        this.sendMessage = this.sendMessage.bind(this);
        this.getSessionId = this.getSessionId.bind(this);
        this.intervalId = null;
        this.eventListenerCleaner = () => {};
        this.tooltipTimeout = null;
    }

    componentDidMount() {
        const { connectOn, autoClearCache, storage, dispatch, defaultHighlightAnimation } =
            this.props;

        // add the default highlight css to the document
        const styleNode = document.createElement('style');
        styleNode.innerHTML = defaultHighlightAnimation;
        document.body.appendChild(styleNode);

        this.intervalId = setInterval(
            () => dispatch(evalUrl(this.props.pageUrl || window.location.href)),
            500
        );
        if (connectOn === 'mount') {
            this.initializeWidget();
            return;
        }

        const localSession = getLocalSession(storage, SESSION_NAME);
        const lastUpdate = localSession ? localSession.lastUpdate : 0;

        if (autoClearCache) {
            if (Date.now() - lastUpdate < 30 * 60 * 1000) {
                this.initializeWidget();
            } else {
                localStorage.removeItem(SESSION_NAME);
            }
        } else {
            this.checkVersionBeforePull();
            dispatch(pullSession());
            if (lastUpdate) this.initializeWidget();
        }
    }

    componentDidUpdate() {
        const { isChatOpen, dispatch, embedded, initialized } = this.props;

        if (isChatOpen) {
            if (!initialized) {
                this.initializeWidget();
            }
            this.trySendInitPayload();
        }

        if (embedded && initialized) {
            dispatch(showChat());
            dispatch(openChat());
        }
    }

    componentWillUnmount() {
        const { socket } = this.props;

        if (socket && socket.isConnected()) {
            socket.close();
        }
        this.tooltipTimeout && clearTimeout(this.tooltipTimeout);
        this.intervalId && clearInterval(this.intervalId);
    }

    getSessionId() {
        const { storage } = this.props;
        // Get the local session, check if there is an existing session_id
        const localSession = getLocalSession(storage, SESSION_NAME);
        const localId = localSession ? localSession.session_id : null;
        return localId;
    }

    sendMessage(payload: any, text = '', when = 'always', tooltipSelector = false) {
        const { dispatch, initialized, messages } = this.props;
        const emit = () => {
            const send = () => {
                dispatch(emitUserMessage(payload));
                if (text !== '') {
                    dispatch(addUserMessage(text, tooltipSelector));
                } else {
                    dispatch(addUserMessage('hidden', tooltipSelector, true));
                }
                if (tooltipSelector) {
                    dispatch(closeChat());
                    showTooltip(true);
                }
            };
            if (when === 'always') {
                send();
            } else if (when === 'init') {
                if (messages.size === 0) {
                    send();
                }
            }
        };
        if (!initialized) {
            this.initializeWidget(false);
            dispatch(initialize());
            emit();
        } else {
            emit();
        }
    }

    handleMessageReceived(messageWithMetadata: any) {
        const { dispatch, isChatOpen, disableTooltips } = this.props;

        // we extract metadata so we are sure it does not interfer with type checking of the message
        const { metadata, ...message } = messageWithMetadata;
        if (!isChatOpen) {
            this.dispatchMessage(message);
            dispatch(newUnreadMessage());
            if (!disableTooltips) {
                dispatch(showTooltip(true));
                this.applyCustomStyle();
            }
        } else if (!this.onGoingMessageDelay) {
            this.onGoingMessageDelay = true;
            dispatch(triggerMessageDelayed(true));
            this.newMessageTimeout(message);
        } else {
            this.messages.push(message);
        }
    }

    popLastMessage() {
        const { dispatch } = this.props;
        if (this.messages.length) {
            this.onGoingMessageDelay = true;
            dispatch(triggerMessageDelayed(true));
            this.newMessageTimeout(this.messages.shift());
        }
    }

    newMessageTimeout(message: any) {
        const { dispatch, customMessageDelay } = this.props;
        this.delayedMessage = message;
        this.messageDelayTimeout = setTimeout(
            () => {
                this.dispatchMessage(message);
                this.delayedMessage = null;
                this.applyCustomStyle();
                dispatch(triggerMessageDelayed(false));
                this.onGoingMessageDelay = false;
                this.popLastMessage();
            },
            customMessageDelay(message.text || '')
        );
    }

    propagateMetadata(metadata: any) {
        const { dispatch } = this.props;
        const {
            linkTarget,
            userInput,
            pageChangeCallbacks,
            domHighlight,
            forceOpen,
            forceClose,
            pageEventCallbacks,
        } = metadata;
        if (linkTarget) {
            dispatch(setLinkTarget(linkTarget));
        }
        if (userInput) {
            dispatch(setUserInput(userInput));
        }
        if (pageChangeCallbacks) {
            dispatch(changeOldUrl(this.props.pageUrl || window.location.href));
            dispatch(setPageChangeCallbacks(pageChangeCallbacks));
        }
        if (domHighlight) {
            dispatch(setDomHighlight(domHighlight));
        }
        if (forceOpen) {
            dispatch(openChat());
        }
        if (forceClose) {
            dispatch(closeChat());
        }
        if (pageEventCallbacks) {
            this.eventListenerCleaner = this.addCustomsEventListeners(
                pageEventCallbacks.pageEvents
            );
        }
    }

    handleBotUtterance(botUtterance: any) {
        const { dispatch } = this.props;
        this.clearCustomStyle();
        this.eventListenerCleaner();
        dispatch(clearMetadata());
        if (botUtterance.metadata) this.propagateMetadata(botUtterance.metadata);
        const newMessage = {
            ...botUtterance,
            text: botUtterance.text ? String(botUtterance.text) : '',
        };
        if (botUtterance.metadata && botUtterance.metadata.customCss) {
            newMessage.customCss = botUtterance.metadata.customCss;
        }
        this.handleMessageReceived(newMessage);
    }

    addCustomsEventListeners(pageEventCallbacks: Array<any>) {
        const eventsListeners: Array<any> = [];

        pageEventCallbacks.forEach((pageEvent) => {
            const { event, payload, selector } = pageEvent;
            const sendPayload = () => {
                this.sendMessage(payload);
            };

            if (event && payload && selector) {
                const elemList = document.querySelectorAll(selector);
                if (elemList.length > 0) {
                    elemList.forEach((elem) => {
                        eventsListeners.push({ elem, event, sendPayload });
                        elem.addEventListener(event, sendPayload);
                    });
                }
            }
        });

        const cleaner = () => {
            eventsListeners.forEach((eventsListener) => {
                eventsListener.elem.removeEventListener(
                    eventsListener.event,
                    eventsListener.sendPayload
                );
            });
        };

        return cleaner;
    }

    clearCustomStyle() {
        const { domHighlight, defaultHighlightClassname } = this.props;
        const domHighlightJS = domHighlight.toJS() || {};
        if (domHighlightJS.selector) {
            const elements = safeQuerySelectorAll(domHighlightJS.selector);
            elements.forEach((element) => {
                switch (domHighlightJS.style) {
                    case 'custom':
                        element.setAttribute('style', '');
                        break;
                    case 'class':
                        element.classList.remove(domHighlightJS.css);
                        break;
                    default:
                        if (defaultHighlightClassname !== '') {
                            element.classList.remove(defaultHighlightClassname);
                        } else {
                            element.setAttribute('style', '');
                        }
                }
            });
        }
    }

    applyCustomStyle() {
        const { domHighlight, defaultHighlightCss, defaultHighlightClassname } = this.props;
        const domHighlightJS = domHighlight.toJS() || {};
        if (domHighlightJS.selector) {
            const elements = safeQuerySelectorAll(domHighlightJS.selector);
            elements.forEach((element) => {
                switch (domHighlightJS.style) {
                    case 'custom':
                        element.setAttribute('style', domHighlightJS.css);
                        break;
                    case 'class':
                        element.classList.add(domHighlightJS.css);
                        break;
                    default:
                        if (defaultHighlightClassname !== '') {
                            element.classList.add(defaultHighlightClassname);
                        } else {
                            element.setAttribute('style', defaultHighlightCss);
                        }
                }
            });
            // We check that the method is here to prevent crashes on unsupported browsers.
            if (elements[0] && elements[0].scrollIntoView) {
                // If I don't use a timeout, the scrollToBottom in messages.jsx
                // seems to override that scrolling
                setTimeout(() => {
                    if (/Mobi/.test(navigator.userAgent)) {
                        elements[0].scrollIntoView({
                            block: 'center',
                            inline: 'nearest',
                            behavior: 'smooth',
                        });
                    } else {
                        const rectangle = elements[0].getBoundingClientRect();

                        const ElemIsInViewPort =
                            rectangle.top >= 0 &&
                            rectangle.left >= 0 &&
                            rectangle.bottom <=
                                (window.innerHeight || document.documentElement.clientHeight) &&
                            rectangle.right <=
                                (window.innerWidth || document.documentElement.clientWidth);
                        if (!ElemIsInViewPort) {
                            elements[0].scrollIntoView({
                                block: 'center',
                                inline: 'nearest',
                                behavior: 'smooth',
                            });
                        }
                    }
                }, 50);
            }
        }
    }

    checkVersionBeforePull() {
        const { storage } = this.props;
        const localSession = getLocalSession(storage, SESSION_NAME);
        if (localSession && localSession.version !== PACKAGE_VERSION_TO_BE_REPLACED) {
            storage.removeItem(SESSION_NAME);
        }
    }

    clearConversation() {
        const { storage } = this.props;

        if (storage) {
            const storageData = JSON.parse(storage.getItem(SESSION_NAME) || '{}');
            storage.setItem(SESSION_NAME, JSON.stringify({ ...storageData, conversation: [] }));
        }
    }

    initializeWidget(sendInitPayload = true) {
        const {
            storage,
            socket,
            dispatch,
            embedded,
            initialized,
            connectOn,
            tooltipPayload,
            tooltipDelay,
            welcomeMessages,
        } = this.props;
        if (!socket.isInitialized()) {
            socket.createSocket();

            socket.on('bot_uttered', (botUttered: any) => {
                // botUttered.attachment.payload.elements = [botUttered.attachment.payload.elements];
                // log.debug(botUttered);
                this.handleBotUtterance(botUttered);
            });

            this.checkVersionBeforePull();

            dispatch(pullSession());

            // Request a session from server
            socket.on('connect', () => {
                const localId = this.getSessionId();
                socket.emitWithoutSession('session_request', { session_id: localId });
            });

            // When session_confirm is received from the server:
            socket.on('session_confirm', (sessionObject: any) => {
                const remoteId =
                    sessionObject && sessionObject.session_id
                        ? sessionObject.session_id
                        : sessionObject;

                // eslint-disable-next-line no-console
                log.debug(`session_confirm:${socket.socket.id} session_id:${remoteId}`);
                // Store the initial state to both the redux store and the storage, set connected to true
                dispatch(connectServer());
                dispatch(setSessionId(remoteId));
                /*
        Check if the session_id is consistent with the server
        If the localId is null or different from the remote_id,
        start a new session.
        */
                const localId = this.getSessionId();
                if (localId !== remoteId) {
                    // storage.clear();
                    this.clearConversation();

                    // Store the received session_id to storage
                    storeLocalSession(storage, SESSION_NAME, remoteId);
                    dispatch(pullSession());
                    if (sendInitPayload) {
                        this.trySendInitPayload();
                    }

                    welcomeMessages && welcomeMessages.map((m) => dispatch(addResponseMessage(m)));
                    // launchIntent && socket.emit('launch_intent', { session_id: remoteId });
                } else {
                    // If this is an existing session, it's possible we changed pages and want to send a
                    // user message when we land.
                    const nextMessage = window.localStorage.getItem(NEXT_MESSAGE);

                    if (nextMessage !== null) {
                        const { message, expiry } = JSON.parse(nextMessage);
                        window.localStorage.removeItem(NEXT_MESSAGE);

                        if (expiry === 0 || expiry > Date.now()) {
                            dispatch(addUserMessage(message));
                            dispatch(emitUserMessage(message));
                        }
                    }
                }
                if (connectOn === 'mount' && tooltipPayload) {
                    this.tooltipTimeout = setTimeout(
                        () => {
                            this.trySendTooltipPayload();
                        },
                        parseInt(`${tooltipDelay}`, 10)
                    );
                }

                // Send session_start event (deduplicated per session)
                const { analyticsServiceUrl } = this.props;
                if (analyticsServiceUrl && !wasSessionStartSent(remoteId)) {
                    // document.referrer is "" for direct visits; omit the field
                    // entirely in that case so the analytics API doesn't have
                    // to interpret an empty string.
                    const ref =
                        this.props.analyticsReferrer ||
                        (typeof document !== 'undefined' && document.referrer
                            ? document.referrer
                            : undefined);
                    sendEvent(
                        analyticsServiceUrl,
                        buildEventPayload(remoteId, 'session_start', { referrer: ref })
                    ).then((res) => {
                        if (res?.success) markSessionStartSent(remoteId);
                    });
                }
            });

            socket.on('disconnect', (reason: string) => {
                // eslint-disable-next-line no-console
                log.debug(reason);
                if (reason !== 'io client disconnect') {
                    dispatch(disconnectServer());
                }
            });
        }

        if (embedded && initialized) {
            dispatch(showChat());
            dispatch(openChat());
        }
    }

    // TODO: Need to erase redux store on load if localStorage
    // is erased. Then behavior on reload can be consistent with
    // behavior on first load

    trySendInitPayload() {
        const {
            initPayload,
            customData,
            socket,
            initialized,
            isChatOpen,
            isChatVisible,
            embedded,
            connected,
            dispatch,
        } = this.props;

        // Send initial payload when chat is opened or widget is shown and initPayload is defined
        if (!initialized && connected && ((isChatOpen && isChatVisible) || embedded)) {
            // Only send initial payload if the widget is connected to the server but not yet initialized

            const sessionId = this.getSessionId();

            // check that session_id is confirmed
            if (!sessionId) return;

            // eslint-disable-next-line no-console
            if (initPayload) {
                log.debug('sending init payload', sessionId);
                socket.emit('user_uttered', {
                    message: initPayload,
                    customData,
                    session_id: sessionId,
                });
            }
            dispatch(initialize());
        }
    }

    trySendTooltipPayload() {
        const { tooltipPayload, socket, customData, connected, isChatOpen, dispatch, tooltipSent } =
            this.props;

        if (connected && !isChatOpen && !tooltipSent.get(tooltipPayload)) {
            const sessionId = this.getSessionId();

            if (!sessionId) return;

            socket.emit('user_uttered', {
                message: tooltipPayload,
                customData,
                session_id: sessionId,
            });

            dispatch(triggerTooltipSent(tooltipPayload));
            dispatch(initialize());
        }
    }

    emitLauncherGreetingsDeeplinks() {
        const {
            socket,
            pendingGreetings,
            showingGreetings,
            tasksBackendClient,
            browserLocation,
            userInfoExists,
            customerId,
            userId,
        } = this.props;

        if (socket) {
            const pendingGreetingsById: Record<string, any> = (pendingGreetings || []).reduce(
                (s, e) => ({ ...s, [e.id]: e }),
                {}
            );

            showingGreetings.map((id) => {
                const greeting = id && pendingGreetingsById[id];

                if (greeting) {
                    if (greeting.deeplink) {
                        log.debug('conveying deeplink (triggered via greeting)', greeting.deeplink);
                        socket.emitOnlyOnNewSession('deeplink', {
                            deeplink: {
                                code: greeting.deeplink.id,
                                params: greeting.deeplink.params,
                            },
                        });
                    }

                    if (tasksBackendClient) {
                        if (userInfoExists && customerId && userId) {
                            tasksBackendClient.postTaskEvent(
                                TaskType.POPUP,
                                id,
                                GreetingTaskEventType.OPEN,
                                browserLocation,
                                { customerId, userId }
                            );
                        } else if (!userInfoExists) {
                            tasksBackendClient.postTaskEvent(
                                TaskType.POPUP,
                                id,
                                GreetingTaskEventType.OPEN,
                                browserLocation
                            );
                        }
                    }
                }
            });
        }
    }

    toggleConversation() {
        const { isChatOpen, dispatch, disableTooltips } = this.props;
        if (isChatOpen && this.delayedMessage) {
            if (!disableTooltips) dispatch(showTooltip(true));
            this.messageDelayTimeout && clearTimeout(this.messageDelayTimeout);
            this.dispatchMessage(this.delayedMessage);
            dispatch(newUnreadMessage());
            this.onGoingMessageDelay = false;
            dispatch(triggerMessageDelayed(false));
            this.messages.forEach((message) => {
                this.dispatchMessage(message);
                dispatch(newUnreadMessage());
            });
            this.applyCustomStyle();

            this.messages = [];
            this.delayedMessage = null;
        } else {
            this.emitLauncherGreetingsDeeplinks();
            this.props.dispatch(showTooltip(false));
            const { analyticsServiceUrl } = this.props;
            const sessionId = this.getSessionId();
            if (analyticsServiceUrl && sessionId) {
                sendEvent(analyticsServiceUrl, buildEventPayload(sessionId, 'chat_opened'));
            }
        }
        this.tooltipTimeout && clearTimeout(this.tooltipTimeout);
        dispatch(toggleChat());
    }

    toggleFullScreen() {
        this.props.dispatch(toggleFullScreen());
    }

    dispatchMessage(message: any) {
        if (Object.keys(message).length === 0) {
            return;
        }
        const { customCss, messageType, ...messageClean } = message;

        log.debug('dispatchMessage', { messageType, keys: Object.keys(messageClean) });
        if (messageType === MESSAGES_TYPES.TEXT) {
            log.debug('isText', messageClean);
            this.props.dispatch(addResponseMessage(messageClean.text, messageClean.infoText));
        } else if (messageType === MESSAGES_TYPES.BUTTONS) {
            log.debug('isButtons', messageClean);
            this.props.dispatch(addButtons(messageClean));
        } else if (messageType === MESSAGES_TYPES.CAROUSEL) {
            log.debug('isCarousel', messageClean);
            this.props.dispatch(addCarousel(messageClean));
        } else if (messageType === MESSAGES_TYPES.VIDREPLY.VIDEO) {
            log.debug('isVideo', messageClean);
            const element = messageClean.attachment.payload;
            this.props.dispatch(
                addVideoSnippet({
                    title: element.title,
                    video: element.src,
                })
            );
        } else if (messageType === MESSAGES_TYPES.IMGREPLY.IMAGE) {
            log.debug('isImage', messageClean);
            const element = messageClean.attachment.payload;
            this.props.dispatch(
                addImageSnippet({
                    title: element.title,
                    image: element.src,
                })
            );
        } else {
            log.debug('isCustom', messageClean);
            // some custom message
            const props = messageClean;
            if (this.props.customComponent) {
                this.props.dispatch(renderCustomComponent(this.props.customComponent, props, true));
            }
        }
        if (customCss) {
            this.props.dispatch(setCustomCss(message.customCss));
        }
    }

    handleMessageSubmit(event: any) {
        event.preventDefault();
        const userUttered = event.target.message.value;
        if (userUttered) {
            this.props.dispatch(addUserMessage(userUttered));
            this.props.dispatch(emitUserMessage(userUttered));
            const { analyticsServiceUrl } = this.props;
            const sessionId = this.getSessionId();
            if (analyticsServiceUrl && sessionId) {
                sendEvent(analyticsServiceUrl, buildEventPayload(sessionId, 'user_message_sent'));
            }
        }
        event.target.message.value = '';
    }

    render() {
        return (
            <ThemeContext.Consumer>
                {(theme) => (
                    <WidgetLayout
                        toggleChat={() => this.toggleConversation()}
                        toggleFullScreen={() => this.toggleFullScreen()}
                        onSendMessage={(event: any) => this.handleMessageSubmit(event)}
                        title={this.props.title}
                        subtitle={this.props.subtitle}
                        customData={this.props.customData}
                        showCloseButton={this.props.showCloseButton}
                        showFullScreenButton={this.props.showFullScreenButton}
                        hideWhenNotConnected={this.props.hideWhenNotConnected}
                        fullScreenMode={this.props.fullScreenMode}
                        //isChatOpen={this.props.isChatOpen}
                        //isChatVisible={this.props.isChatVisible}
                        badge={this.props.badge}
                        embedded={this.props.embedded}
                        overlayMode={this.props.overlayMode}
                        chatWindowInsetX={this.props.chatWindowInsetX}
                        chatWindowInsetY={this.props.chatWindowInsetY}
                        disablePreviewBubble={this.props.disablePreviewBubble}
                        mobileFullScreenOnFocus={this.props.mobileFullScreenOnFocus}
                        params={this.props.params}
                        openLauncherImage={this.props.openLauncherImage}
                        inputTextFieldHint={this.props.inputTextFieldHint}
                        closeImage={this.props.closeImage}
                        customComponent={this.props.customComponent}
                        displayUnreadCount={this.props.displayUnreadCount}
                        showMessageDate={this.props.showMessageDate}
                        tooltipPayload={this.props.tooltipPayload}
                        showTitleBar={theme.showTitleBar !== false}
                    />
                )}
            </ThemeContext.Consumer>
        );
    }
}

//const mapDispatchToProps = (dispatch, ownProps) => ({
//  dispatch,
//  registerAuthtokenChangeHandler: (handler) => dispatch(registerAuthtokenChangeHandler(handler))
//});

const mapStateToProps = (state: any) => ({
    initialized: state.behavior.get('initialized'),
    connected: state.behavior.get('connected'),
    isChatOpen: state.behavior.get('isChatOpen'),
    isChatVisible: state.behavior.get('isChatVisible'),
    fullScreenMode: state.behavior.get('fullScreenMode'),
    tooltipSent: state.metadata.get('tooltipSent'),
    oldUrl: state.behavior.get('oldUrl'),
    pageChangeCallbacks: state.behavior.get('pageChangeCallbacks'),
    domHighlight: state.metadata.get('domHighlight'),
    messages: state.messages,
    showingGreetings: state.behavior.get('showingGreetings', Set()),
    tasksBackendClient: state.config.get('tasksBackendClient'),
    browserLocation: configSelectors.browserLocation(state.config),
    userInfoExists: state.config.get('userInfoExists'),
    customerId: state.config.get('customerId'),
    userId: state.config.get('userId'),
    pendingGreetings: behaviorSelectors.pendingGreetings(state.behavior),
    analyticsServiceUrl: state.config.get('analyticsServiceUrl') || null,
    sessionId: state.config.get('sessionId') || null,
});

Widget.propTypes = {
    ref: PropTypes.func,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    customData: PropTypes.shape({}),
    handleNewUserMessage: PropTypes.func,
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    initPayload: PropTypes.string,
    showCloseButton: PropTypes.bool,
    showFullScreenButton: PropTypes.bool,
    hideWhenNotConnected: PropTypes.bool,
    connectOn: PropTypes.oneOf(['mount', 'open']),
    autoClearCache: PropTypes.bool,
    fullScreenMode: PropTypes.bool,
    isChatVisible: PropTypes.bool,
    isChatOpen: PropTypes.bool,
    showingGreetings: PropTypes.any,
    badge: PropTypes.number,
    socket: PropTypes.shape({}),
    embedded: PropTypes.bool,
    mobileFullScreenOnFocus: PropTypes.bool,
    params: PropTypes.shape({}),
    connected: PropTypes.bool,
    initialized: PropTypes.bool,
    openLauncherImage: PropTypes.string,
    closeImage: PropTypes.string,
    inputTextFieldHint: PropTypes.string,
    customComponent: PropTypes.func,
    displayUnreadCount: PropTypes.bool,
    showMessageDate: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    customMessageDelay: PropTypes.func.isRequired,
    tooltipPayload: PropTypes.string,
    tooltipSent: PropTypes.shape({}),
    tooltipDelay: PropTypes.number.isRequired,
    domHighlight: PropTypes.shape({}),
    storage: PropTypes.shape({}),
    disableTooltips: PropTypes.bool,
    defaultHighlightAnimation: PropTypes.string,
    defaultHighlightCss: PropTypes.string,
    defaultHighlightClassname: PropTypes.string,
    welcomeMessages: PropTypes.arrayOf(PropTypes.string),
    messages: PropTypes.any,
    tasksBackendClient: PropTypes.any,
    browserLocation: PropTypes.shape({}),
    userInfoExists: PropTypes.bool,
    customerId: PropTypes.string,
    userId: PropTypes.string,
};

Widget.defaultProps = {
    isChatOpen: false,
    isChatVisible: true,
    showingGreetings: Set(),
    fullScreenMode: false,
    connectOn: 'mount',
    autoClearCache: false,
    displayUnreadCount: false,
    tooltipPayload: null,
    inputTextFieldHint: 'Type a message...',
    oldUrl: '',
    disableTooltips: false,
    defaultHighlightClassname: '',
    defaultHighlightCss:
        'animation: 0.5s linear infinite alternate default-botfront-blinker-animation; outline-style: solid;',
    // unfortunately it looks like outline-style is not an animatable property on Safari
    defaultHighlightAnimation: `@keyframes default-botfront-blinker-animation {
    0% {
      outline-color: rgba(0,255,0,0);
    }
    49% {
      outline-color: rgba(0,255,0,0);
    }
    50% {
      outline-color:green;
    }
    100% {
      outline-color: green;
    }
  }`,
    customMessageDelay: (message: string) => 100,
    tooltipDelay: 500,
};

export default connect(
    mapStateToProps,
    null, //mapDispatchToProps,
    null,
    { forwardRef: true }
)(Widget);
